import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";

import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import CorrectAddressList from "./CorrectAddressList";
import ShippingTypeTabs from "./ShippingTypeTabs";

export default function ShipAddressValidator({
  isAddMode,
  isWorkOrder = false,
  submitFunction,
  shippingAddress,
  showModal,
}) {
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const [address, setAddress] = useState(null);
  const [errors, setErrors] = useState([]);
  const [checkMarks, setCheckMarks] = useState({
    signatureRelease: false,
    selfCollect: false,
  });

  const [correctAddressModal, setCorrectAddressModal] = useState(false);
  const [correctAddressesList, setCorrectAddressesList] = useState(null);
  const [loading, setLoading] = useState(false);

  /*   All Functions
   ********************************************* */

  const handleCorrectAddressSelect = (correctAddress) => {
    setAddress({
      ...address,
      addressType: correctAddress?.AddressClassification?.Description,
      addressLine1: correctAddress?.AddressKeyFormat?.AddressLine[0],
      addressLine2: correctAddress?.AddressKeyFormat?.AddressLine[1],
      city: correctAddress?.AddressKeyFormat?.PoliticalDivision2,
      state: correctAddress?.AddressKeyFormat?.PoliticalDivision1,
      zip: correctAddress?.AddressKeyFormat?.PostcodePrimaryLow,
      country: correctAddress?.AddressKeyFormat?.CountryCode,
    });
  };

  const validateAddress = async (e) => {
    e.preventDefault();
    setErrors([]);

    // Required fields validation
    if (
      !address?.country ||
      !address?.city ||
      !address?.state ||
      !address?.zip ||
      !address?.addressLine1
    ) {
      dispatch(
        setNotification({
          message: "Please fill all required address fields",
          type: "error",
        }),
      );
      return;
    }

    try {
      setLoading(true);
      setErrors([]);
      const { data } = await API.post(
        "/customers/validate-address",
        {
          addressLine1: address?.addressLine1,
          addressLine2: address?.addressLine2 || "",
          city: address?.city,
          state: address?.state,
          zip: address?.zip,
          country: address?.country,
        },
        {
          headers: authHeader(),
        },
      );

      if (data?.success) {
        if (data?.addressType === "Unknown") {
          setErrors([...errors, "Address type is unknown"]);
          dispatch(
            setNotification({
              message: "Invalid address",
              type: "error",
            }),
          );
          return { success: false };
        }

        setAddress({
          ...address,
          addressType: data?.correctAddress?.AddressClassification?.Description,
          addressLine1: data?.correctAddress?.AddressKeyFormat?.AddressLine[0],
          addressLine2: data?.correctAddress?.AddressKeyFormat?.AddressLine[1],
          city: data?.correctAddress?.AddressKeyFormat?.PoliticalDivision2,
          state: data?.correctAddress?.AddressKeyFormat?.PoliticalDivision1,
          zip: data?.correctAddress?.AddressKeyFormat?.PostcodePrimaryLow,
          country: data?.correctAddress?.AddressKeyFormat?.CountryCode,
          addressGroundTransitDays: data?.transitDays,
        });

        dispatch(
          setNotification({
            message: `Address validated: ${
              data.isResidential ? "Residential" : "Commercial"
            } address`,
            type: "success",
          }),
        );

        return { success: true, data: data };
      } else {
        if (data?.correctAddress) {
          setCorrectAddressModal(true);
          setCorrectAddressesList(data?.correctAddress);
          return { success: false };
        }
        dispatch(
          setNotification({
            message: "Address validation failed",
            type: "error",
          }),
        );
        return { success: false };
      }
    } catch (err) {
      console.error("Address validation error:", err);
      dispatch(
        setNotification({
          message: err.message || "Address validation failed",
          type: "error",
        }),
      );
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    setAddress({
      ...shippingAddress,
      country: shippingAddress?.country || "US",
    });
    setCheckMarks({
      signatureRelease: shippingAddress?.signatureRelease,
      selfCollect: shippingAddress?.selfCollect,
    });
  }, [shippingAddress]);

  return (
    <fieldset disabled={loading}>
      {loading && <Spinner />}

      <form
        onSubmit={(e) => {
          // Work order shipping only: the whole left column is required except
          // Address 2. The UPS/DHL panel on the right (address type, ground
          // transit days) is NOT -- those are filled by Validate, not the user,
          // and an address is saveable without ever calling UPS. Scoped to
          // isWorkOrder so the customer- and lead-address forms that share this
          // component keep their own rules.
          if (isWorkOrder) {
            const required = [
              ["Customer", address?.addressCompany],
              ["Address 1", address?.addressLine1],
              ["City", address?.city],
              ["State", address?.state],
              ["ZIP", address?.zip],
              ["Country", address?.country],
            ];
            const missing = required
              .filter(([, v]) => !String(v || "").trim())
              .map(([label]) => label);
            if (missing.length) {
              e.preventDefault();
              dispatch(
                setNotification({
                  message: `Please fill the required field${
                    missing.length === 1 ? "" : "s"
                  }: ${missing.join(", ")}`,
                  type: "error",
                }),
              );
              return;
            }
          }
          submitFunction(e, address, checkMarks);
        }}
      >
        <div className="row">
          {/*   Left Panel
           ********************************************* */}
          <div className="col-6">
            {/* Address Company Name */}
            <div className="row mb-1">
              <div className="flex-center justify-content-start col-4 pe-0">
                <label className="form-label">Customer {isWorkOrder && <span className="text-danger">*</span>}</label>
              </div>
              <div className="col-8">
                <input
                  value={
                    isWorkOrder
                      ? address?.addressCompany
                      : address?.companyName || ""
                  }
                  onChange={(e) =>
                    setAddress({
                      ...address,
                      [isWorkOrder ? "addressCompany" : "companyName"]:
                        e.target.value,
                    })
                  }
                  placeholder="Enter customer name"
                  type="text"
                  className="form-control"
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="flex-center justify-content-start col-4 pe-0">
                <label className="form-label">Address 1 {isWorkOrder && <span className="text-danger">*</span>}</label>
              </div>
              <div className="col-8">
                <input
                  value={address?.addressLine1 || ""}
                  onChange={(e) =>
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      addressLine1: e.target.value,
                    })
                  }
                  placeholder="Enter address 1"
                  type="text"
                  className="form-control"
                />
              </div>
            </div>

            {/* Address 2 */}
            <div className="row mb-1">
              <div className="flex-center justify-content-start col-4 pe-0">
                <label className="form-label">Address 2</label>
              </div>
              <div className="col-8">
                <input
                  value={address?.addressLine2 || ""}
                  onChange={(e) =>
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      addressLine2: e.target.value,
                    })
                  }
                  placeholder="Enter address 2"
                  type="text"
                  className="form-control"
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="flex-center justify-content-start col-4 pe-0">
                <label className="form-label">City | State | Zip {isWorkOrder && <span className="text-danger">*</span>}</label>
              </div>
              <div className="d-flex col-8">
                <input
                  type="text"
                  className="form-control me-1"
                  placeholder="Enter City"
                  value={address?.city || ""}
                  onChange={(e) => {
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      city: e.target.value,
                    });
                  }}
                />

                <input
                  type="text"
                  className="form-control me-1"
                  placeholder="Enter State"
                  value={address?.state || ""}
                  onChange={(e) => {
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      state: e.target.value,
                    });
                  }}
                />

                <input
                  type="text"
                  className="form-control me-1"
                  placeholder="Enter Zip"
                  value={address?.zip || ""}
                  onChange={(e) => {
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      zip: e.target.value,
                    });
                  }}
                />
              </div>
            </div>

            <div className="row mb-1">
              <div className="flex-center justify-content-start col-4 pe-0">
                <label className="form-label">Country ( US Only ) {isWorkOrder && <span className="text-danger">*</span>} </label>
              </div>
              <div className="col-8">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter Country"
                  value={address?.country || ""}
                  onChange={(e) => {
                    setAddress({
                      ...address,
                      addressType: "",
                      addressGroundTransitDays: null,
                      country: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
          </div>
          {/*   Right Panel
           ********************************************* */}

          <div className="col-6 d-flex flex-column">
            <ShippingTypeTabs
              shippingAddress={address}
              setShippingAddress={setAddress}
              checkMarks={checkMarks}
              setCheckMarks={setCheckMarks}
              validateAddress={validateAddress}
            />

            {/* Errors Section */}
            {errors.length > 0 && (
              <div className="alert alert-danger">
                <div className="d-flex align-items-center justify-content-between">
                  <h5 className="alert-heading">Errors</h5>
                  <button
                    className="btn-close btn-close-primary"
                    onClick={() => setErrors([])}
                  ></button>
                </div>
                <ul className="mb-0">
                  {errors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          {/* Buttons */}
          <div className="mb-1 flex-grow-1 d-flex justify-content-end align-items-end">
            <div>
              <button
                onClick={() => showModal(false)}
                type="button"
                className="btn btn-secondary me-1"
              >
                Close
              </button>
              <button type="submit" className="btn btn-primary">
                {isAddMode ? "Add" : "Update"}
              </button>
            </div>
          </div>
        </div>
      </form>
      {/*   Correct Address List Modal
       ********************************************* */}
      <Modal
        size="lg"
        show={correctAddressModal}
        onHide={() => setCorrectAddressModal(false)}
        contentClassName="bg-transparent"
      >
        <Modal.Body className="px-4 bg-light custom-shadow-sm custom-border-radius">
          {loading && <Spinner />}
          <div className="d-flex justify-content-between align-items-center">
            <h3>Correct Address List</h3>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setCorrectAddressModal(false)}
            >
              Close
            </button>
          </div>
          <hr />
          <CorrectAddressList
            showModal={setCorrectAddressModal}
            correctAddressesList={correctAddressesList}
            handleCorrectAddressSelect={handleCorrectAddressSelect}
          />
        </Modal.Body>
      </Modal>
    </fieldset>
  );
}
